/**
 * Modal for viewing another user's profile
 */
import { useState, useEffect } from 'react';
import type { DAGObject, ProfileContent, ArticleContent } from '@amorphous/core';
import { Avatar } from './Avatar';
import './ProfileModal.css';

interface ProfileModalProps {
  pubkey: string;
  profile: DAGObject<ProfileContent> | null;
  onClose: () => void;
  onFriend?: (pubkey: string) => void;
  onBlock?: (pubkey: string) => void;
  isFriend?: boolean;
  isBlocked?: boolean;
  getUserStats: (pubkey: string) => Promise<{
    karma: number;
    profileAge: number;
    articleCount: number;
    commentCount: number;
  }>;
  getArticlesByUser?: (pubkey: string) => Promise<DAGObject<ArticleContent>[]>;
  isOwnProfile?: boolean;
}

export function ProfileModal({
  pubkey,
  profile,
  onClose,
  onFriend,
  onBlock,
  isFriend = false,
  isBlocked = false,
  getUserStats,
  getArticlesByUser,
  isOwnProfile = false,
}: ProfileModalProps) {
  const [stats, setStats] = useState<{
    karma: number;
    profileAge: number;
    articleCount: number;
    commentCount: number;
  } | null>(null);
  const [recentArticles, setRecentArticles] = useState<DAGObject<ArticleContent>[]>([]);

  const displayName = profile?.content?.displayName || `${pubkey.slice(0, 8)}...`;
  const bio = profile?.content?.bio;
  const avatarUrl = profile?.content?.avatarDataUrl;

  useEffect(() => {
    getUserStats(pubkey).then(setStats);
    if (getArticlesByUser) {
      getArticlesByUser(pubkey).then(articles => {
        setRecentArticles(articles.slice(0, 5));
      });
    }
  }, [pubkey, getUserStats, getArticlesByUser]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatAge = (days: number) => {
    if (days === 0) return 'New member';
    if (days === 1) return '1 day';
    if (days < 30) return `${days} days`;
    if (days < 365) return `${Math.floor(days / 30)} months`;
    return `${Math.floor(days / 365)} years`;
  };

  return (
    <div className="profile-modal-overlay" onClick={handleOverlayClick}>
      <div className="profile-modal">
        <button className="modal-close-btn" onClick={onClose}>x</button>

        <div className="profile-modal-header">
          <Avatar
            src={avatarUrl}
            displayName={displayName}
            size="large"
          />
          <h2 className="profile-modal-name">{displayName}</h2>
          {stats && (
            <div className="profile-modal-stats">
              <span className="stat-item" title="Karma">
                {stats.karma >= 0 ? '+' : ''}{stats.karma} karma
              </span>
              <span className="stat-divider">|</span>
              <span className="stat-item" title="Member age">
                {formatAge(stats.profileAge)}
              </span>
            </div>
          )}
        </div>

        {bio && (
          <div className="profile-modal-bio">
            {bio}
          </div>
        )}

        {stats && (
          <div className="profile-modal-activity">
            <span>{stats.articleCount} posts</span>
            <span>{stats.commentCount} comments</span>
          </div>
        )}

        {!isOwnProfile && (onFriend || onBlock) && (
          <div className="profile-modal-actions">
            {onFriend && (
              <button
                className={`action-btn friend-btn ${isFriend ? 'active' : ''}`}
                onClick={() => onFriend(pubkey)}
              >
                {isFriend ? '- Remove Friend' : '+ Add Friend'}
              </button>
            )}
            {onBlock && (
              <button
                className={`action-btn block-btn ${isBlocked ? 'active' : ''}`}
                onClick={() => onBlock(pubkey)}
              >
                {isBlocked ? 'Unblock' : 'Block'}
              </button>
            )}
          </div>
        )}

        {recentArticles.length > 0 && (
          <div className="profile-modal-posts">
            <h3>Recent Posts</h3>
            <ul className="recent-posts-list">
              {recentArticles.map(article => (
                <li key={article.hash} className="recent-post-item">
                  {article.content?.title || 'Untitled'}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="profile-modal-pubkey">
          <span className="mono">{pubkey.slice(0, 16)}...{pubkey.slice(-8)}</span>
        </div>
      </div>
    </div>
  );
}
